import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Popover, Input, Typography, Table, Checkbox, Space, Divider, Modal, Select } from 'antd';
import { ExportOutlined, SearchOutlined, FileTextOutlined, ClearOutlined, PlusOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import DynamicFormSP from './DynamicFormSP.component';
import { fieldEntityActionConfig } from './utils/fieldEntityActionConfig';

const { Text } = Typography;

const SingleEntitySelectMiniature = ({
    singularEntity = '',
    pluralEntity = '',
    constantPrefix = '',
    listEntities,
    listEntitiesByIds,
    onChange,
    value, 
    primarySearchProp = '',
    secondarySearchProp = '',
    placeholder = 'None selected',
    navigateUrl = '',
    navigateLabel = '',
    isMultiSelect = false,
    enableCreate = true,
    enableEditing = true,
    icon = <FileTextOutlined />
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchText, setSearchText] = useState('');
    const [searchProp, setSearchProp] = useState(primarySearchProp || '');
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [error, setError] = useState(null);
    
    // Safely capitalize the entity name
    const capitalizedSingularEntity = singularEntity ? singularEntity.charAt(0).toUpperCase() + singularEntity.slice(1) : '';
    const entityConfig = capitalizedSingularEntity ? 
        (fieldEntityActionConfig[`${capitalizedSingularEntity}Ids`] || fieldEntityActionConfig[`${capitalizedSingularEntity}Id`]) 
        : null;
    
    // Normalize value to always be an array internally
    const normalizedValue = useMemo(() => {
        if (isMultiSelect) {
            return Array.isArray(value) ? value : value ? [value] : [];
        }
        return value ? [value] : [];
    }, [value, isMultiSelect]);

    const [selectedEntities, setSelectedEntities] = useState([]);

    const [entityListFilter, setEntityListFilter] = useState({
        query: '',
        page: 1,
        limit: 10,
        filter: searchProp || primarySearchProp || ''
    });

    // Get entities from Redux state
    const entityList = useSelector((state) => state[`${singularEntity}List`] || {});
    const { loading: loadingList, [pluralEntity]: entities = [] } = entityList;

    // Get entities by IDs from Redux state
    const entityListByIds = useSelector((state) => state[`${singularEntity}ListByIds`] || {});
    const { loading: loadingListByIds, [pluralEntity]: entitiesByIds = [] } = entityListByIds;

    // Load selected entities by IDs when normalizedValue changes
    // OPTIMIZATION: Only call listEntitiesByIds when we have IDs and are in multi-select mode
    useEffect(() => {
        if (normalizedValue.length > 0 && isMultiSelect && listEntitiesByIds) {
            dispatch(listEntitiesByIds(normalizedValue));
        }
        // Always reset selectedEntities when normalizedValue is empty
        if (normalizedValue.length === 0) {
            setSelectedEntities([]);
        }
    }, [dispatch, listEntitiesByIds, normalizedValue, isMultiSelect]);

    // Update selectedEntities when entitiesByIds changes (only for multi-select)
    useEffect(() => {
        if (isMultiSelect && normalizedValue.length > 0 && entitiesByIds?.length > 0) {
            // Only update with entities that match our normalizedValue
            const validEntities = entitiesByIds.filter(entity => 
                normalizedValue.includes(entity.id)
            );
            setSelectedEntities(validEntities);
        }
    }, [entitiesByIds, normalizedValue, isMultiSelect]);

    // OPTIMIZATION: Remove automatic loading of entities on mount
    // Entities will be loaded only when popover opens

    // Update search prop when primarySearchProp or secondarySearchProp changes
    useEffect(() => {
        setSearchProp(primarySearchProp || '');
        setEntityListFilter(prev => ({
            ...prev,
            filter: primarySearchProp || ''
        }));
    }, [primarySearchProp]);

    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            const newFilter = {
                ...entityListFilter,
                query: searchValue || '',
                page: 1
            };
            setEntityListFilter(newFilter);
            
            // OPTIMIZATION: Only trigger search if popover is open and we have listEntities
            if (popoverVisible && listEntities) {
                dispatch(listEntities(newFilter));
            }
        }, 300),
        [entityListFilter, popoverVisible, listEntities, dispatch]
    );

    const handleSearch = (e) => {
        const searchValue = e.target.value;
        setSearchText(searchValue);
        debouncedSearch(searchValue);
    };

    const handleSearchPropChange = (value) => {
        setSearchProp(value);
        const newFilter = { 
            ...entityListFilter, 
            filter: value, 
            query: searchText 
        };
        setEntityListFilter(newFilter);
        
        // OPTIMIZATION: Only trigger search if popover is open and we have listEntities
        if (popoverVisible && listEntities) {
            dispatch(listEntities(newFilter));
        }
    };

    const handlePopoverOpenChange = (visible) => {
        setPopoverVisible(visible);
        
        // OPTIMIZATION: Only load entities when popover opens
        if (visible && listEntities) {
            dispatch(listEntities(entityListFilter));
        }
    };

    const handleNavigateClick = () => {
        if (navigateUrl) {
            window.open(navigateUrl, '_blank');
            setPopoverVisible(false);
        }
    };

    const handleItemSelect = useCallback((entityId, entity) => {
        if (isMultiSelect) {
            const isCurrentlySelected = normalizedValue.includes(entityId);
            const newSelectedIds = isCurrentlySelected
                ? normalizedValue.filter(id => id !== entityId)
                : [...normalizedValue, entityId];

            const newSelectedEntities = isCurrentlySelected
                ? selectedEntities.filter(e => e.id !== entityId)
                : [...selectedEntities, entity];

            onChange?.(newSelectedIds, newSelectedEntities);
        } else {
            onChange?.(entityId, entity);
            setPopoverVisible(false);
        }
    }, [isMultiSelect, normalizedValue, selectedEntities, onChange]);

    const getButtonText = useCallback(() => {
        // If no values selected, show placeholder
        if (!normalizedValue?.length) return placeholder;
        
        // For single select, we can show the value directly without waiting for selectedEntities
        if (!isMultiSelect && normalizedValue.length === 1) {
            // Try to find the entity in the main entities list first (if already loaded)
            const entity = entities.find(e => e.id === normalizedValue[0]);
            if (entity) {
                return entity[primarySearchProp];
            }
            // OPTIMIZATION: For single select, show a simple identifier instead of loading data
            // This prevents unnecessary API calls just to display the button text
            return `${t('Selected')} (${normalizedValue[0].substring(0, 8)}...)`;
        }
        
        // For multi-select, handle loading states properly
        if (isMultiSelect) {
            // If selectedEntities are not yet loaded but we have normalizedValue, show count
            if (normalizedValue.length > 0 && selectedEntities.length === 0) {
                return normalizedValue.length === 1 
                    ? `${t('Selected')} (1)` 
                    : `${normalizedValue.length} ${t('selected')}`;
            }
            
            // If selectedEntities don't match normalizedValue count, show count from normalizedValue
            if (selectedEntities.length !== normalizedValue.length) {
                return normalizedValue.length === 1 
                    ? (selectedEntities[0]?.[primarySearchProp] || `${t('Selected')} (1)`)
                    : `${selectedEntities[0]?.[primarySearchProp] || t('Item')} +${normalizedValue.length - 1}`;
            }
            
            // Normal case when selectedEntities are fully loaded
            return selectedEntities.length === 1 
                ? selectedEntities[0][primarySearchProp]
                : `${selectedEntities[0][primarySearchProp]} +${selectedEntities.length - 1}`;
        }
        
        // Fallback for single select
        return placeholder;
    }, [selectedEntities, normalizedValue, isMultiSelect, primarySearchProp, placeholder, t, entities]);

    const handleClearAll = useCallback(() => {
        if (isMultiSelect) {
            onChange([], []);
        } else {
            onChange(null, null);
            setPopoverVisible(false);
        }
    }, [isMultiSelect, onChange]);

    const showModal = () => {
        setPopoverVisible(false);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleCreate = (newEntity) => {
        if (entityConfig?.createEntity && dispatch) {
            try {
                dispatch(entityConfig.createEntity(newEntity));
                dispatch(listEntities(entityListFilter));
                setIsModalVisible(false);
            } catch (err) {
                setError('Failed to create entity');
                console.error('Error creating entity:', err);
            }
        }
    };

    // Table Configuration
    const columns = useMemo(() => [
        isMultiSelect && {
            key: 'selection',
            width: 48,
            render: (_, record) => (
                <Checkbox
                    checked={normalizedValue.includes(record.id)}
                    onChange={() => handleItemSelect(record.id, record)}
                    onClick={e => e.stopPropagation()}
                />
            )
        },
        {
            key: 'content',
            render: (_, record) => (
                <span>
                    {record[primarySearchProp]}
                    {record[secondarySearchProp] && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            | {record[secondarySearchProp]}
                        </Text>
                    )}
                </span>
            )
        }
    ].filter(Boolean), [isMultiSelect, normalizedValue, handleItemSelect, primarySearchProp, secondarySearchProp]);

    // Combine and deduplicate entities
    const allEntities = useMemo(() => 
        [...entities, ...selectedEntities].filter((entity, index, self) => 
            index === self.findIndex(e => e.id === entity.id)
        ), [entities, selectedEntities]);

    // Create search options based on available props
    const searchOptions = useMemo(() => {
        const options = [];
        if (primarySearchProp) {
            options.push({ value: primarySearchProp, label: t(primarySearchProp) });
        }
        if (secondarySearchProp) {
            options.push({ value: secondarySearchProp, label: t(secondarySearchProp) });
        }
        return options;
    }, [primarySearchProp, secondarySearchProp, t]);

    const popoverContent = (
        <div style={{ width: 300 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input
                    prefix={<SearchOutlined />}
                    placeholder={t('Search')}
                    value={searchText}
                    onChange={handleSearch}
                    style={{ flex: 1 }}
                />
                {searchOptions.length > 1 && (
                    <Select
                        placeholder={t("Search by")}
                        style={{ width: '100px', height: '32px' }}
                        size="small"
                        value={searchProp}
                        onChange={handleSearchPropChange}
                    >
                        {searchOptions.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {normalizedValue.length > 0 && (
                    <Button
                        type="text"
                        onClick={handleClearAll}
                        size="small"
                        style={{ 
                            color: 'var(--ant-color-error)',
                            padding: '0 4px'
                        }}
                    >
                        {t('Clear')}
                    </Button>
                )}
            </div>
            <Table
                size="small"
                dataSource={allEntities}
                columns={columns}
                pagination={false}
                showHeader={false}
                rowKey="id"
                loading={isMultiSelect ? (loadingList || loadingListByIds) : loadingList}
                onRow={(record) => ({
                    onClick: (e) => {
                        if (!e.target.closest('input')) {
                            handleItemSelect(record.id, record);
                        }
                    },
                    style: { 
                        cursor: 'pointer',
                        backgroundColor: normalizedValue.includes(record.id)
                            ? 'var(--ant-color-primary-bg)'
                            : undefined
                    }
                })}
                style={{ 
                    maxHeight: 300,
                    overflow: 'auto'
                }}
            />
            {enableCreate && enableEditing && entityConfig && (
                <>
                    <Divider style={{ margin: '8px 0' }} />
                    <div 
                        onClick={showModal}
                        style={{ 
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            ':hover': {
                                backgroundColor: 'var(--ant-color-primary-bg)'
                            }
                        }}
                    >
                        <PlusOutlined />
                        <span>{t(`Create New ${singularEntity}`)}</span>
                    </div>
                </>
            )}
            {navigateUrl && (
                <>
                    <Divider style={{ margin: '8px 0' }} />
                    <div 
                        onClick={handleNavigateClick}
                        style={{ 
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            ':hover': {
                                backgroundColor: 'var(--ant-color-primary-bg)'
                            }
                        }}
                    >
                        <ExportOutlined />
                        <span>{navigateLabel || t('View all')}</span>
                    </div>
                </>
            )}
        </div>
    );

    if (error) {
        return <Text type="danger">{error}</Text>;
    }

    return (
        <>
            <Popover
                content={popoverContent}
                trigger="click"
                open={popoverVisible}
                onOpenChange={handlePopoverOpenChange}
                placement="bottomLeft"
            >
                <Button 
                    type="text"
                    loading={isMultiSelect ? (loadingList || loadingListByIds) : loadingList}
                    style={{ 
                        minWidth: 200, 
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    {icon}
                    <span style={{ 
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMultiSelect ? 'normal' : 'nowrap'
                    }}>
                        {getButtonText()}
                    </span>
                </Button>
            </Popover>

            {entityConfig && enableEditing && (
                <Modal
                    title={t(`Create New ${singularEntity}`)}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width="80%"
                    destroyOnClose
                >
                    <DynamicFormSP
                        singularEntity={singularEntity}
                        pluralEntity={pluralEntity}
                        constantPrefix={constantPrefix}
                        loadDto={entityConfig.loadDto}
                        createEntity={handleCreate}
                        updateEntity={entityConfig.updateEntity}
                        deleteEntity={entityConfig.deleteEntity}
                        getEntityDetails={entityConfig.getEntityDetails}
                        listEntities={entityConfig.listEntities}
                        listEntitiesByIds={entityConfig.listEntitiesByIds}
                        defaultEditMode="Create"
                        fieldsToHide={['Id']}
                        isParent={true}
                        
                        enableCreate={true}
                        enableSearchBar={false}
                        enableActionButtons={false}
                    />
                </Modal>
            )}
        </>
    );
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
    // Compare primitive props
    const primitiveProps = [
        'singularEntity',
        'pluralEntity',
        'constantPrefix',
        'primarySearchProp',
        'secondarySearchProp',
        'placeholder',
        'navigateUrl',
        'navigateLabel',
        'isMultiSelect',
        'enableCreate',
        'enableEditing'
    ];

    const primitiveEqual = primitiveProps.every(prop => prevProps[prop] === nextProps[prop]);
    if (!primitiveEqual) return false;

    // Compare value arrays
    const prevValue = Array.isArray(prevProps.value) ? prevProps.value : [prevProps.value].filter(Boolean);
    const nextValue = Array.isArray(nextProps.value) ? nextProps.value : [nextProps.value].filter(Boolean);
    
    if (prevValue.length !== nextValue.length) return false;
    if (!prevValue.every((val, idx) => val === nextValue[idx])) return false;

    // Compare functions by reference
    const functionProps = ['listEntities', 'listEntitiesByIds', 'onChange'];
    if (!functionProps.every(prop => prevProps[prop] === nextProps[prop])) return false;

    // Compare icon
    return prevProps.icon?.type === nextProps.icon?.type;
};

export default React.memo(SingleEntitySelectMiniature, arePropsEqual);
